import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { motion } from 'motion/react'
import { Absolute, Flex } from 'mds-web-ui'
import { NewActionIcon, ChangeColorIcon } from './icons'
import { CircularButton } from './CircularButton'
import { listMainButtonMotionProps } from './buttonPoses'
import { ThreeDotsIcon } from './icons/ThreeDots'
import { TOOLTIP_MAIN } from '../utils/tooltipIds'

const ListChooseColorButton = (props) => (
  <CircularButton
    aria-label="Choose colour"
    data-tooltip-id={TOOLTIP_MAIN}
    data-tooltip-content="Choose list colour"
    data-tooltip-delay-show={300}
    {...props}
  >
    <ChangeColorIcon />
  </CircularButton>
)

const ListSettingsButton = (props) => (
  <CircularButton
    aria-label="List settings"
    data-tooltip-id={TOOLTIP_MAIN}
    data-tooltip-content="List settings"
    data-tooltip-delay-show={300}
    {...props}
  >
    <ThreeDotsIcon size={26} />
  </CircularButton>
)

const ListAddActionButton = forwardRef(({ color, ...props }, ref) => (
  <CircularButton
    aria-label="Create Action"
    data-tooltip-id={TOOLTIP_MAIN}
    data-tooltip-html="Create Action<br/><small>(hold shift to create at top)<small/>"
    data-tooltip-delay-show={500}
    width={100}
    color={color}
    borderRadius={6} // same as Action cards
    {...props}
    ref={ref}
  >
    <NewActionIcon color={color} />
  </CircularButton>
))

const MotionListAddActionButton = motion.create(ListAddActionButton)

export const PosedListAddActionButton = forwardRef((props, ref) => {
  const { newActionIsVisible, pose, initialPose: _initialPose, ...rest } = props
  const pressed =
    newActionIsVisible === true ||
    (newActionIsVisible == null && pose === 'pressed')
  // The pressed (hidden) animation lives on a wrapper so the button's
  // whileHover/whileTap gesture props stay constant. Toggling gesture props
  // between an object and `false` while the pointer is over the button leaves
  // motion's gesture state stuck, so the button never scales back up after
  // cancelling a new Action (e.g. pressing escape).
  return (
    <motion.div
      initial={false}
      animate={
        pressed
          ? {
              scale: 0,
              y: -30,
              transition: { scale: { type: 'tween', duration: 0.15 } },
            }
          : { scale: 1, y: 0 }
      }
    >
      <MotionListAddActionButton
        ref={ref}
        {...listMainButtonMotionProps}
        {...rest}
      />
    </motion.div>
  )
})

export const ListButtons = React.memo(
  ({
    color,
    bg,
    onAddButtonClick,
    onSettingsButtonClick,
    onChooseColorButtonClick,
    newActionIsVisible,
  }) => (
    <Absolute bottom={0} left={0} right={0} pb={45}>
      <Flex px={4} justifyContent="space-between">
        <ListSettingsButton
          color={color}
          bg={bg}
          onClick={onSettingsButtonClick}
        />
        <PosedListAddActionButton
          color={color}
          bg={bg}
          onClick={onAddButtonClick}
          newActionIsVisible={newActionIsVisible}
          className={null} // don't auto-hide on lists
        />
        <ListChooseColorButton
          color={color}
          bg={bg}
          onClick={onChooseColorButtonClick}
        />
      </Flex>
    </Absolute>
  ),
)

ListButtons.propTypes = {
  bg: PropTypes.string,
  onChooseColorButtonClick: PropTypes.func,
  onAddButtonClick: PropTypes.func,
}

ListButtons.displayName = 'ListButtons'
